import React, { createContext, useReducer, useContext, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import styled from 'styled-components';
import { Flex, Text, Box, Button } from '@urbaninfrastructure/react-ui-kit';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _taggedTemplateLiteralLoose(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }

  strings.raw = raw;
  return strings;
}

function _templateObject() {
  var data = _taggedTemplateLiteralLoose(["\n  ", "\n"]);

  _templateObject = function _templateObject() {
    return data;
  };

  return data;
}
var initialState = [];

function toastReducer(state, action) {
  switch (action.type) {
    case 'ADD_TOAST':
      {
        if (state.some(function (toast) {
          return toast.id === action.payload.id;
        })) {
          return state;
        }

        return [].concat(state, [action.payload]);
      }

    case 'REMOVE_TOAST':
      {
        return state.filter(function (toast) {
          return action.payload !== toast.id;
        });
      }

    default:
      return state;
  }
}

var ToastContext = /*#__PURE__*/createContext(undefined);
var ToastProvider = function ToastProvider(props) {
  var reducer = useReducer(toastReducer, initialState);
  return React.createElement(ToastContext.Provider, {
    value: reducer
  }, props.children);
};
function useToasts() {
  var context = useContext(ToastContext);

  if (context === undefined) {
    throw new Error('useToasts must be used within a ToastProvider');
  }

  var toasts = context[0],
      dispatch = context[1];
  /** Displays a toast until ttl (time to live) expires */

  function addToast(_ref) {
    var _ref$id = _ref.id,
        id = _ref$id === void 0 ? Math.random().toString() : _ref$id,
        _ref$ttl = _ref.ttl,
        ttl = _ref$ttl === void 0 ? 3000 : _ref$ttl,
        toast = _objectWithoutPropertiesLoose(_ref, ["id", "ttl"]);

    dispatch({
      type: 'ADD_TOAST',
      payload: _extends({
        id: id,
        ttl: ttl
      }, toast)
    });
  }
  /** Remove a toast by its id */


  function removeToast(id) {
    dispatch({
      type: 'REMOVE_TOAST',
      payload: id
    });
  }

  return {
    toasts: toasts,
    addToast: addToast,
    removeToast: removeToast
  };
}

var Toast = function Toast(_ref2) {
  var toast = _ref2.toast,
      dispatch = _ref2.dispatch;
  var id = toast.id,
      ttl = toast.ttl,
      primaryAction = toast.primaryAction;
  useEffect(function () {
    if (!ttl) {
      return;
    }

    var timeoutId = setTimeout(function () {
      dispatch({
        type: 'REMOVE_TOAST',
        payload: id
      });
    }, ttl);
    return function () {
      clearTimeout(timeoutId);
    };
  }, [id, ttl, dispatch]);
  return React.createElement(Flex, {
    p: "xs",
    mb: 2,
    borderRadius: "md",
    maxWidth: {
      _: 400,
      lg: 600
    },
    boxShadow: "heavy",
    bg: toast.type ? "state." + toast.type : 'white',
    color: toast.type ? '#222' : 'text',
    alignItems: "center",
    flexDirection: {
      _: 'column',
      md: 'row'
    },
    mx: "auto"
  }, React.createElement(Text, {
    typoStyle: "xxs"
  }, toast.text), primaryAction && React.createElement(Box, {
    mt: {
      _: 2,
      md: 0
    },
    ml: {
      _: 0,
      md: 2
    }
  }, React.createElement(Button, {
    onClick: primaryAction.onClick,
    variant: "primary",
    size: "small"
  }, primaryAction.text)));
};

var ToastBox = /*#__PURE__*/styled(Box)( /*#__PURE__*/_templateObject(), function (_ref3) {
  var theme = _ref3.theme;
  return "\n      " + theme.mediaQueries[0] + " {\n        transform: translateX(-50%);\n      }\n  ";
});
var Toasts = function Toasts() {
  var context = useContext(ToastContext);

  if (context === undefined) {
    throw new Error('Toasts must be used within a ToastProvider');
  }

  var toasts = context[0],
      dispatch = context[1];
  return React.createElement(ToastBox, {
    "data-testid": "Toasts",
    position: "fixed",
    top: 0,
    left: {
      sm: '50%'
    },
    width: {
      _: '95%',
      sm: 'auto'
    },
    zIndex: 1000,
    mt: "sm"
  }, React.createElement(AnimatePresence, {
    initial: true
  }, toasts.map(function (toast) {
    return React.createElement(motion.div, {
      key: toast.id,
      layout: "position",
      initial: {
        opacity: 0,
        y: -50
      },
      animate: {
        opacity: 1,
        y: 0
      },
      exit: {
        opacity: 0,
        transition: {
          duration: 0.2
        }
      }
    }, React.createElement(Toast, {
      dispatch: dispatch,
      toast: toast
    }));
  })));
};

export { ToastContext, ToastProvider, Toasts, useToasts };
//# sourceMappingURL=toasts.esm.js.map
